import React, { useState } from 'react';
import { X } from 'lucide-react';
import { redeemCoupon } from '../../services/api/coupons';
import useAuthStore from '../../store/authStore';

interface RedeemCouponModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RedeemCouponModal: React.FC<RedeemCouponModalProps> = ({ isOpen, onClose }) => {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { login } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      const response = await redeemCoupon(code);
      
      if (response.success) {
        setSuccess(response.message || 'Coupon redeemed successfully!');
        const user = useAuthStore.getState().user;
        if (user && response.newBalance !== undefined) {
          login(localStorage.getItem('auth-token') || '', {
            ...user,
            credits: response.newBalance
          });
        }
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to redeem coupon');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-xl font-semibold text-gray-900">Redeem Coupon</h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm">
                {success}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Coupon Code
                </label>
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                  placeholder="Enter your coupon code"
                  required
                  maxLength={20}
                  disabled={isSubmitting}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
              >
                {isSubmitting ? 'Redeeming...' : 'Redeem Coupon'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RedeemCouponModal;